<script setup>
import { ref, computed } from 'vue';
import { useEventListener } from '@vueuse/core';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'edit',
    validator: value => ['alert', 'edit'].includes(value),
  },
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  cancelButtonLabel: {
    type: String,
    default: '',
  },
  confirmButtonLabel: {
    type: String,
    default: '',
  },
  confirmButtonColor: {
    type: String,
    default: '',
  },
  disableConfirmButton: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  showCancelButton: {
    type: Boolean,
    default: true,
  },
  showConfirmButton: {
    type: Boolean,
    default: true,
  },
  overflowYAuto: {
    type: Boolean,
    default: false,
  },
  width: {
    type: String,
    default: 'lg',
    validator: value =>
      ['6xl', '5xl', '4xl', '3xl', '2xl', 'xl', 'lg', 'md', 'sm'].includes(
        value
      ),
  },
  position: {
    type: String,
    default: 'center',
    validator: value => ['center', 'top'].includes(value),
  },
});

const emit = defineEmits(['confirm', 'close']);

const { t } = useI18n();
const isOpen = ref(false);

const maxWidthClass = computed(() => {
  const classesMap = {
    '6xl': 'max-w-6xl',
    '5xl': 'max-w-5xl',
    '4xl': 'max-w-4xl',
    '3xl': 'max-w-3xl',
    '2xl': 'max-w-2xl',
    xl: 'max-w-xl',
    lg: 'max-w-lg',
    md: 'max-w-md',
    sm: 'max-w-sm',
  };
  return classesMap[props.width] ?? 'max-w-md';
});

const open = () => {
  isOpen.value = true;
};

const close = () => {
  emit('close');
  isOpen.value = false;
};

// Don't close if a native dialog (e.g., ProseMirror prompt) is open on top.
const handleBackdropClick = () => {
  if (document.querySelectorAll('dialog[open]').length > 0) return;
  close();
};

const confirm = () => {
  emit('confirm');
};

useEventListener(document, 'keydown', e => {
  if (e.key === 'Escape' && isOpen.value) {
    if (document.querySelectorAll('dialog[open]').length === 0) close();
  }
});

defineExpose({ open, close });
</script>

<template>
  <TeleportWithDirection to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex justify-center bg-n-alpha-black1 backdrop-blur-[4px]"
      :class="
        position === 'top'
          ? 'items-start pt-[clamp(2rem,5vh,5rem)]'
          : 'items-center'
      "
      @click="handleBackdropClick"
    >
      <div
        class="w-full transition-all duration-300 ease-in-out shadow-xl rounded-xl"
        :class="[
          maxWidthClass,
          overflowYAuto ? 'overflow-y-auto max-h-[90vh]' : 'overflow-visible',
        ]"
        @click.stop
      >
        <form
          class="flex flex-col w-full h-auto gap-6 p-6 overflow-visible text-start align-middle transition-all duration-300 ease-in-out transform bg-n-alpha-3 backdrop-blur-[100px] shadow-xl rounded-xl"
          @submit.prevent="confirm"
        >
          <div v-if="title || description" class="flex flex-col gap-2">
            <h3 class="text-base font-medium leading-6 text-n-slate-12">
              {{ title }}
            </h3>
            <slot name="description">
              <p v-if="description" class="mb-0 text-sm text-n-slate-11">
                {{ description }}
              </p>
            </slot>
          </div>
          <slot v-if="isOpen" />
          <slot name="footer">
            <div
              v-if="showCancelButton || showConfirmButton"
              class="flex items-center justify-between w-full gap-3"
            >
              <Button
                v-if="showCancelButton"
                variant="faded"
                color="slate"
                :label="cancelButtonLabel || t('DIALOG.BUTTONS.CANCEL')"
                class="w-full"
                type="button"
                @click="close"
              />
              <Button
                v-if="showConfirmButton"
                :color="
                  confirmButtonColor || (type === 'edit' ? 'blue' : 'ruby')
                "
                :label="confirmButtonLabel || t('DIALOG.BUTTONS.CONFIRM')"
                class="w-full"
                :is-loading="isLoading"
                :disabled="disableConfirmButton || isLoading"
                type="submit"
              />
            </div>
          </slot>
        </form>
      </div>
    </div>
  </TeleportWithDirection>
</template>
